import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from "react";
import { useAuth } from "../../contexts/AuthContext";
import styles from "./home.module.scss";
import { Button } from "../Common/Button";
import { GoogleSignIn } from "./GoogleSignIn";
import { EmptyPageSection } from "components/Common/Section/EmptyPageSection";
import { useIFrame } from "contexts/IframeContext";
import { COMPENDIUM_PATHS } from "components/Compendium/consts/routeConsts";
import { Navigate } from "@tanstack/react-router";
import { usePage } from "hooks/usePage";
import { SetupUser } from "./SetupUser";
import { ResetPassword } from "./ResetPassword";
import { usePrevious } from "react-use";
import { BaseAccountForm } from "./BaseAccountForm";
import { GoogleSignUp } from "./GoogleSignUp";
import { HelpCircle, TriangleAlert } from "lucide-react";
import { Tooltip } from "components/Common/Tooltip/Tooltip";
import { usePreloadImage } from "hooks/usePreloadImage";
import { INTRO_START_SPLASH_SRC } from "components/Tutorial/steps/introTutorialConsts";
import { usePortal } from "contexts/PortalContext";
import { Modal } from "components/Common/Modal";
import { BaseImage, Image } from "components/Common/Image/Image";
import mascot from "components/Footer/mascot.png";
import { getRandIndex, getWrapIndex } from "lib/arrayUtils";
import {
  BLOODLINE_COMPENDIUM_IMAGES,
  ORIGIN_COMPENDIUM_IMAGES,
  POST_COMPENDIUM_IMAGES,
} from "components/Compendium/images";
import { reverse } from "lodash";
import classNames from "classnames/bind";
import { BoolSerializers, useLocalStorageState } from "hooks/useLocalStorage";
import { useStateRef } from "hooks/useStateRef";
import { useSmartRef } from "hooks/useSmartRef";

const classNameBuilder = classNames.bind(styles);

const HOME_MODES = {
  CREATE: "CREATE",
  RESET: "RESET",
};

const RIGHT_IMAGES = [
  ...BLOODLINE_COMPENDIUM_IMAGES,
  ...ORIGIN_COMPENDIUM_IMAGES,
  ...POST_COMPENDIUM_IMAGES,
];
const LEFT_IMAGES = reverse(RIGHT_IMAGES);

const MarqueeColumn = ({ images, className, stopAnimation, startIndex }) => {
  const finalImages = images;
  const finalImagesRef = useSmartRef(finalImages);
  const [targetImage, setTargetImage] = useStateRef(startIndex);
  const prevImage = getWrapIndex(targetImage, -1, finalImages.length);
  const wrapImage = getWrapIndex(targetImage, 1, finalImages.length);

  useEffect(() => {
    if (stopAnimation) {
      return;
    }
    const timer = setInterval(() => {
      setTargetImage((prev) =>
        getWrapIndex(prev, 1, finalImagesRef.current.length)
      );
    }, 5000);

    return () => {
      clearTimeout(timer);
    };
  });

  return (
    <div className={classNameBuilder("marquee-column", className)}>
      <div
        className={classNameBuilder("marquee-content", { stop: stopAnimation })}
      >
        {images.map((s, i) => {
          const isPrev = i === prevImage;
          const isNext = i === wrapImage;
          const isTarget = i === targetImage;

          if (!isPrev && !isTarget && !isNext) {
            return null;
          }

          return (
            <BaseImage
              contain
              key={s}
              src={s}
              className={classNameBuilder("marquee-image", {
                next: isNext,
                prev: isPrev,
              })}
            />
          );
        })}
      </div>
    </div>
  );
};

const localModeTooltipText = (
  <>
    <div>
      By clicking this button, you will enter the app in "Demo Mode". It will
      allow you to use the app with data stored locally on your machine. Thus,
      no other users can see this data. If you want to be able to interact with
      other users, you need to make an account.
    </div>
    <br />
    <div>
      Warning: There is no dedicated mechanism to port data from Demo mode to
      the real database. If you want to re-use a sheet you created during Demo
      Mode, you can save it using CTRL + S, then import it to a group on the
      Groups Page.
    </div>
  </>
);

export const Home = ({ children }) => {
  const {
    readUser,
    authLoaded,
    userId,
    loading,
    user,
    signIn,
    createUser,
    error,
    setError,
    enableLocalMode,
    authUser,
    shouldSetupUser,
    setShouldSetupUser,
  } = useAuth();
  const { isIFrame } = useIFrame();
  const [base] = usePage();
  const [mode, setMode] = useState(null);
  const prevMode = usePrevious(mode);
  const portalProps = usePortal();
  const [stopAnimation, setStopAnimation] = useLocalStorageState(
    "stopAnimation",
    {
      ...BoolSerializers,
      defaultValue: false,
    }
  );
  const leftStart = useMemo(() => getRandIndex(LEFT_IMAGES), []);
  const rightStart = useMemo(() => getRandIndex(RIGHT_IMAGES), []);

  usePreloadImage(INTRO_START_SPLASH_SRC);

  useEffect(() => {
    if (prevMode !== mode) {
      setError(null);
    }
  }, [mode, prevMode, setError]);

  useLayoutEffect(() => {
    if (!isIFrame) {
      readUser();
    }
  }, [userId, readUser, isIFrame]);

  const onSignInClick = useCallback(
    (email, password) =>
      signIn({
        email,
        password,
      }),
    [signIn]
  );

  const onSignUpClick = useCallback(
    async (email, password) => {
      try {
        await createUser({ email, password });
        setMode(null);
      } catch (err) {
        console.error(err);
      }
    },
    [createUser]
  );

  const goBack = () => setMode(null);
  const BackButton = (
    <Button onClick={goBack} className={styles["back"]} whiteText>
      Back
    </Button>
  );

  if (isIFrame && base !== COMPENDIUM_PATHS.BASE) {
    return <Navigate to={`/${COMPENDIUM_PATHS.BASE}`} />;
  }

  if (!isIFrame && !authLoaded && userId) {
    return (
      <EmptyPageSection
        label={"Please wait a moment..."}
        delayedContentClassName={styles["delayed-content"]}
        delayedContent={
          <div className={styles["delayed"]}>
            <i>There seems to be an issue with authentication!</i>
            <div className={styles["red-error"]}>
              {error?.code || "Unknown Error"}
            </div>
            <div>
              If refreshing the page does not resolve this issue, please try
              clicking the button below
            </div>
            <Button
              onClick={() => {
                localStorage.clear();
                window.location.reload();
              }}
            >
              Clear local storage and refresh
            </Button>
            <div>
              If the button does not fix the issue, please send an email to{" "}
              <u>charsmithapp@gmail.com</u> and we'll try to get it resolved as
              soon as possible
            </div>
          </div>
        }
        delayedContentTimeout={8000}
      >
        Checking Authentication
      </EmptyPageSection>
    );
  }

  if (shouldSetupUser) {
    return <SetupUser user={user} setShouldSetupUser={setShouldSetupUser} />;
  }

  if (isIFrame || (authUser && user)) {
    return children;
  }

  let props = {};
  let shouldShowImages = true;

  if (loading) {
    shouldShowImages = false;
    props = {
      children: "Signing in...",
      shouldDisableMargin: true,
    };
  } else if (mode === HOME_MODES.CREATE) {
    props = {
      children: (
        <BaseAccountForm
          error={error}
          submitLabel={"Create account"}
          onSubmit={onSignUpClick}
          googleButton={<GoogleSignUp />}
          onGoogleClick={signIn}
        >
          <div className={styles["warning"]}>
            <TriangleAlert />
            If you use a fake email, we will not be able to reset your password!
          </div>
        </BaseAccountForm>
      ),
      icon: null,
      headerSuffix: BackButton,
      label: "Create a new account",
    };
  } else if (mode === HOME_MODES.RESET) {
    props = {
      children: <ResetPassword goBack={goBack} />,
      icon: null,
      headerSuffix: BackButton,
      label: "Reset your Password",
      textContainerClassName: styles["reset"],
    };
  } else {
    props = {
      icon: null,
      children: (
        <BaseAccountForm
          error={error}
          loading={loading}
          submitLabel={"Sign In"}
          onSubmit={onSignInClick}
          googleButton={<GoogleSignIn />}
          onGoogleClick={signIn}
        >
          <u
            onClick={() => setMode(HOME_MODES.RESET)}
            className={styles["reset-link"]}
          >
            Forgot password?
          </u>
        </BaseAccountForm>
      ),
      underSection: (
        <div className={styles["under-section"]}>
          <Modal
            portalProps={portalProps}
            title="Entering Demo Mode"
            onFooterSubmit={() => {
              portalProps.setIsPortalComponentRendered(false);
              enableLocalMode();
            }}
            submitText={"Confirm"}
            contentClassName={styles["demo-modal"]}
          >
            {localModeTooltipText}
          </Modal>
          <div className={styles["prompt"]}>New to Charsmith?</div>
          <Button
            onClick={() => setMode(HOME_MODES.CREATE)}
            colorful
            className={styles["button"]}
          >
            <div>Create your account here!</div>
          </Button>
          <div>OR</div>
          <Button
            onClick={() => {
              portalProps.setIsPortalComponentRendered(true);
            }}
            colorful
            className={styles["button"]}
          >
            Enter Demo Mode
            <HelpCircle
              style={{
                marginLeft: "5px",
                marginBottom: "2px",
                verticalAlign: "top",
                width: "16px",
              }}
            />
            <Tooltip>{localModeTooltipText}</Tooltip>
          </Button>
        </div>
      ),
    };
  }

  return (
    <div
      className={classNameBuilder("container", {
        "no-margin": props.shouldDisableMargin,
      })}
    >
      {shouldShowImages && (
        <>
          <Image
            contain
            src={mascot}
            className={styles["image"]}
            imageClassName={styles["image-inner"]}
          />
          <MarqueeColumn
            images={RIGHT_IMAGES}
            startIndex={rightStart}
            className={styles["right-column"]}
            stopAnimation={stopAnimation}
          />
          <MarqueeColumn
            images={LEFT_IMAGES}
            startIndex={leftStart}
            className={styles["left-column"]}
            stopAnimation={stopAnimation}
          />
          <Button
            onClick={() => setStopAnimation(!stopAnimation)}
            className={styles["stop-animation-button"]}
          >
            {stopAnimation ? "Enable Animation" : "Disable Animation"}
          </Button>
        </>
      )}
      <div>
        <EmptyPageSection
          containerClassName={classNameBuilder({
            "content-container": shouldShowImages,
          })}
          label={"Sign in to get started!"}
          {...props}
        />
      </div>
    </div>
  );
};
